import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { getEnrollments, enrollUserToDevices, removeEnrollment, retryEnrollment, setEnrollmentStatus } from '@/api/enrollments'
import { getDevices, type Device } from '@/api/devices'
import type { UserDeviceEnrollment } from '@veridia/shared'

const STATUS_LABELS: Record<string, string> = {
  active: 'activo',
  pending: 'en cola',
  blocked: 'bloqueado',
}

const STATUS_CLASSES: Record<string, string> = {
  active: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400',
  pending: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-400',
  blocked: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400',
}

interface Props {
  userId: string
}

export function AccessDevicesTab({ userId }: Props) {
  const [enrollments, setEnrollments] = useState<UserDeviceEnrollment[]>([])
  const [allDevices, setAllDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([])
  const [pendingRemove, setPendingRemove] = useState<{ userId: string; deviceId: string; deviceName: string } | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const pollRef = useRef<NodeJS.Timeout | null>(null)

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)
    try {
      const [enrs, { devices }] = await Promise.all([getEnrollments(userId), getDevices()])
      setEnrollments(enrs)
      setAllDevices(devices)
    } catch {
      if (!silent) toast.error('Error al cargar dispositivos de acceso')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [userId])

  useEffect(() => { loadData() }, [loadData])

  // Auto-poll mientras haya enrollments pendientes
  useEffect(() => {
    const hasPending = enrollments.some(e => e.status === 'pending')
    if (hasPending) {
      pollRef.current = setInterval(() => loadData(true), 5000)
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [enrollments, loadData])

  const enrolledDeviceIds = useMemo(() => new Set(enrollments.map(e => e.device_id)), [enrollments])
  const availableDevices = useMemo(
    () => allDevices.filter(d => !enrolledDeviceIds.has(d.id)),
    [allDevices, enrolledDeviceIds],
  )

  async function handleConfirmRemove() {
    if (!pendingRemove) return
    const { userId: uid, deviceId, deviceName } = pendingRemove
    setPendingRemove(null)
    try {
      await removeEnrollment(uid, deviceId)
      setEnrollments(prev => prev.map(e =>
        e.device_id === deviceId ? { ...e, status: 'blocked' as const } : e
      ))
      toast.success(`Acceso removido de ${deviceName}`)
    } catch {
      toast.error('Error al remover acceso')
    }
  }

  async function handleReactivate(deviceId: string) {
    try {
      await enrollUserToDevices(userId, [deviceId])
      setEnrollments(prev => prev.map(e =>
        e.device_id === deviceId ? { ...e, status: 'pending' as const } : e
      ))
    } catch {
      toast.error('Error al reactivar acceso')
    }
  }

  async function handleRetry(deviceId: string) {
    setActionLoading(deviceId + ':retry')
    try {
      await retryEnrollment(userId, deviceId)
      setEnrollments(prev => prev.map(e =>
        e.device_id === deviceId ? { ...e, status: 'pending' as const } : e
      ))
      toast.success('Sincronización reintentada')
    } catch {
      toast.error('Error al reintentar sincronización')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleMarkActive(deviceId: string) {
    setActionLoading(deviceId + ':active')
    try {
      await setEnrollmentStatus(userId, deviceId, 'active')
      setEnrollments(prev => prev.map(e =>
        e.device_id === deviceId ? { ...e, status: 'active' as const } : e
      ))
      toast.success('Marcado como activo')
    } catch {
      toast.error('Error al actualizar estado')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleAssign() {
    if (selectedDeviceIds.length === 0) return
    try {
      const result = await enrollUserToDevices(userId, selectedDeviceIds)
      const newEnrollments: UserDeviceEnrollment[] = result.enrollments.map(e => ({
        id: e.id,
        user_id: userId,
        device_id: e.device_id,
        device_name: allDevices.find(d => d.id === e.device_id)?.name,
        enrolled_at: new Date().toISOString(),
        status: 'pending' as const,
      }))
      setEnrollments(prev => [...prev, ...newEnrollments])
      setSelectedDeviceIds([])
      setAssignOpen(false)
    } catch {
      toast.error('Error al asignar dispositivos')
    }
  }

  function toggleDevice(deviceId: string) {
    setSelectedDeviceIds(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
    )
  }

  if (loading) return <p className="text-text-muted text-sm">Cargando dispositivos...</p>

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-text-primary text-sm font-medium">Dispositivos de acceso</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            title="Refrescar estado"
            aria-label="Refrescar estado"
            className="inline-flex items-center gap-1 text-text-muted text-xs hover:text-text-primary transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {availableDevices.length > 0 && (
            <button
              onClick={() => setAssignOpen(true)}
              aria-label="Asignar dispositivos"
              className="inline-flex items-center gap-1.5 border border-border text-text-muted text-xs px-3 py-1.5 rounded-md hover:text-text-primary hover:border-accent/50 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Asignar
            </button>
          )}
        </div>
      </div>

      {enrollments.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-border rounded-lg">
          <p className="text-text-muted text-sm mb-3">Sin dispositivos asignados</p>
          {availableDevices.length > 0 && (
            <button
              onClick={() => setAssignOpen(true)}
              className="inline-flex items-center gap-1.5 bg-accent text-background text-xs font-semibold px-4 py-2 rounded-md hover:bg-accent-hover transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Asignar dispositivo
            </button>
          )}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {enrollments.map(enr => (
            <li
              key={enr.device_id}
              className="flex items-center justify-between p-3 bg-background border border-border rounded-lg"
            >
              <div className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${enr.status === 'active' ? 'bg-emerald-400' : 'bg-text-muted opacity-40'}`}
                />
                <span className="text-text-primary text-sm font-medium">
                  {enr.device_name ?? enr.device_id}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_CLASSES[enr.status] ?? STATUS_CLASSES.pending}`}>
                  {STATUS_LABELS[enr.status] ?? enr.status}
                </span>
                {enr.status === 'active' && (
                  <button
                    onClick={() => setPendingRemove({ userId, deviceId: enr.device_id, deviceName: enr.device_name ?? enr.device_id })}
                    aria-label="Quitar acceso"
                    className="text-xs text-text-muted hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    Quitar
                  </button>
                )}
                {enr.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRetry(enr.device_id)}
                      disabled={actionLoading === enr.device_id + ':retry'}
                      title="Reintentar sincronización"
                      className="text-xs text-accent hover:text-accent-hover disabled:opacity-40 transition-colors"
                    >
                      {actionLoading === enr.device_id + ':retry' ? '...' : 'Reintentar'}
                    </button>
                    <button
                      onClick={() => handleMarkActive(enr.device_id)}
                      disabled={actionLoading === enr.device_id + ':active'}
                      title="Marcar como activo sin sincronizar"
                      className="text-xs text-text-muted hover:text-text-primary disabled:opacity-40 transition-colors"
                    >
                      {actionLoading === enr.device_id + ':active' ? '...' : 'Activar'}
                    </button>
                  </div>
                )}
                {enr.status === 'blocked' && (
                  <button
                    onClick={() => handleReactivate(enr.device_id)}
                    className="text-xs text-accent hover:text-accent-hover transition-colors"
                  >
                    Reactivar
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Assign modal */}
      {assignOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setAssignOpen(false)}>
          <div className="bg-surface border border-border rounded-lg p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-text-primary font-semibold text-base mb-4">Asignar dispositivos</h2>
            {availableDevices.length === 0 ? (
              <p className="text-text-muted text-sm">Todos los dispositivos ya están asignados.</p>
            ) : (
              <ul className="flex flex-col gap-2 mb-4">
                {availableDevices.map(dev => (
                  <li key={dev.id} className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={`assign-${dev.id}`}
                      aria-label={dev.name}
                      role="checkbox"
                      checked={selectedDeviceIds.includes(dev.id)}
                      onChange={() => toggleDevice(dev.id)}
                      className="w-4 h-4 accent-accent"
                    />
                    <label htmlFor={`assign-${dev.id}`} className="text-text-primary text-sm cursor-pointer flex-1">
                      {dev.name}
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setAssignOpen(false); setSelectedDeviceIds([]) }}
                className="text-xs border border-border text-text-muted px-3 py-1.5 rounded-md hover:text-text-primary transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAssign}
                disabled={selectedDeviceIds.length === 0}
                aria-label="Guardar"
                className="text-xs bg-accent text-background font-semibold px-3 py-1.5 rounded-md hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!pendingRemove} onOpenChange={() => setPendingRemove(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Quitar acceso a este dispositivo?</AlertDialogTitle>
          <AlertDialogDescription>El miembro no podrá acceder hasta ser reasignado.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemove}>
              Quitar acceso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
